import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import ServicesPreview from '../components/ServicesPreview';
import './SalonServicesView.css';

/**
 * Read-only view of a salon's LIVE published service list. Admins can no
 * longer edit services directly here — updates go through the
 * request/prepare/review workflow in Service Update Requests.
 */
function SalonServicesView() {
  const { salonId } = useParams();
  const navigate = useNavigate();

  const [salonName, setSalonName] = useState('');
  const [serviceCategories, setServiceCategories] = useState([]);
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoadError('');
      const salonSnap = await getDoc(doc(db, 'salons', salonId));
      if (!salonSnap.exists()) {
        setLoadError('Salon not found');
        return;
      }
      const data = salonSnap.data();
      setSalonName(data.profile?.shopName || 'Unknown Salon');
      const servicesData = data.profile?.servicesData || {};
      setServiceCategories(servicesData.serviceCategories || []);
      setCombos(servicesData.combos || []);
    } catch (err) {
      setLoadError('Failed to load. Please try again.');
    }
  }, [salonId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await load();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    await load();
    setSyncing(false);
  };

  if (loading) {
    return (
      <div className="ssview-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading services...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="ssview-page">
        <div className="error-container">
          <p className="error-text">{loadError}</p>
          <button className="retry-btn" onClick={() => navigate('/salons')}>Back to Salons</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ssview-page">
      <div className="ssview-header">
        <div className="ssview-header-info">
          <button className="ssview-back-btn" onClick={() => navigate('/salons')}>← Salons</button>
          <div>
            <h2>{salonName}</h2>
            <p className="ssview-subtitle">
              Read-only view of the salon's live, published service list
            </p>
          </div>
        </div>
        <button className="ssview-sync-btn" onClick={handleSync} disabled={syncing}>
          {syncing ? 'Syncing...' : '⟳ Sync'}
        </button>
      </div>

      <div className="ssview-info-banner">
        This screen is read-only. To change a salon's services, go to{' '}
        <button className="ssview-link-btn" onClick={() => navigate('/service-requests')}>
          Service Update Requests
        </button>{' '}
        — admin edits only happen through a request the salon initiates.
      </div>

      <ServicesPreview serviceCategories={serviceCategories} combos={combos} />
    </div>
  );
}

export default SalonServicesView;
